/**
 * Copyright (c) 2010-2020 California CAD Solutions, Inc. Licensed subject
 * to an End-User License Agreement included in a separate file named EULA.txt
 * that is distributed with this file.
 */
import { Injectable } from '@angular/core';

import { catchError, map, mergeMap, withLatestFrom } from 'rxjs/operators';

import { GsptResponse, MapQueryPropertyFilter } from '../../types';
import { RestService } from '../../modules/shared/services/rest.service';
import { QueryService } from '../../modules/map/services/query.service';
import { LoggerService } from '../../modules/shared/services/logger.service';
import { AppConfigState } from '../../core/state/appConfig/app-config.state';
import { Store } from '@ngxs/store';
import { combineLatest, Observable, of } from 'rxjs';

@Injectable({providedIn: 'root'})
export class GsptService {
    constructor(
        private logger: LoggerService,
        private rest: RestService,
        private queryService: QueryService,
        private store: Store
    ) {}

    public query(mapName: string, layerName: string, node: any, distance: number, direction: string) {
        const gsptParams = {
            direction,
            node,
        };
        // distance is optional
        if (distance) {
            gsptParams['distance'] = distance;
        }

        const route = `gspt/query/${ mapName }/${ layerName }`;
        return this.rest.apiGet(route, gsptParams).pipe(
            catchError((err, caught$) => {
                this.logger.error(err);
                return caught$;
            }),
            mergeMap((data: GsptResponse) => {
                // expect string[] of segment ids
                const filter: MapQueryPropertyFilter = {
                    filter: {
                        property: this.getGsptLayer(mapName, layerName).idField,
                        values: data.segments,
                    },
                    type: 'property',
                };
                return this.queryService.doQuery(filter, false, true, layerName);
            })
        );
    }

    public getSelectedFeature(mapName, layerName): Observable<any> {
        const gsptLayer = this.getGsptLayer(mapName, layerName);
        if (!gsptLayer) {
            return of(null);
        }
        return this.queryService
            .getSelection(layerName)
            .pipe(map((selection) => selection[0].fields[gsptLayer.idField]));
    }

    public isGsptEnabled$(mapName$: Observable<string>, layerName$: Observable<string>): Observable<boolean> {
        return layerName$.pipe(
            mergeMap((layerName) => this.queryService.getSelection(layerName)),
            withLatestFrom(mapName$, layerName$),
            map(([selection, mapName, layerName]) => {
                let enabled = false;
                if (this.gsptLayers) {
                    if (this.gsptLayers.length > 0) {
                        const gsptLayer = this.gsptLayers.find(
                            (l) => l.mapName === mapName && l.layer === layerName
                        );
                        enabled = !!gsptLayer && selection.length === 1;
                    }
                }
                return enabled;
            })
        );
    }

    private getGsptLayer(mapName, layerName) {
        let gsptLayer = null;
        if (this.gsptLayers && this.gsptLayers.length > 0) {
            gsptLayer = this.gsptLayers.find((l) => l.mapName === mapName && l.layer === layerName);
        }
        return gsptLayer;
    }

    get gsptLayers() {
        return this.store.selectSnapshot(AppConfigState.gspt);
    }
}
